import dayjs from "dayjs";
import relativeTime from "dayjs/esm/plugin/relativeTime";
import "dayjs/locale/sv";

import { ChartPie, FileCheck, FileLock, HandCoins, UserX } from "lucide-react";
import { getI18n } from "react-i18next";
import { stdnum } from "stdnum";

import { CountryCode, type SupportedLanguage } from "@krea/shared/types/common";
import { amountValuesList as amountValuesListShared } from "@krea/shared/utils";

import ImportantEuLoanIcon from "../images/svg/important-info/eu-loan-flag.svg";
import {
  type ILenderResponseBenefit,
  LenderResponseBenefitTypeEnum,
} from "../types/lenderResponse";

export { minFinancingAmountPerCountry } from "@krea/shared/utils/formConstants";
export { orgNumValidator } from "@krea/shared/utils/orgNumValidator";
export { privacyPolicyLink } from "@krea/shared/utils/privacyPolicyLink";

export const amountValuesList = amountValuesListShared;

dayjs.extend(relativeTime as dayjs.PluginFunc);

const NOTIFICATION_RECEIVER = {
  CONTACT_PERSON: "CONTACT_PERSON",
  PARTNER: "PARTNER",
  CONTACT_PERSON_AND_PARTNER: "CONTACT_PERSON_AND_PARTNER",
};

const BANKID_FLOW_STATES = {
  PENDING: "pending",
  FAILED: "failed",
  COMPLETE: "complete",
};

const COMPANY_TYPE = {
  LIMITED_COMPANY: "LIMITED_COMPANY",
  PARTNERSHIP: "PARTNERSHIP",
  SOLE_TRADER: "SOLE_TRADER",
};

const CUSTOMER_RATES = {
  FIRST_MONTH_RATE: "firstMonthInterestRate",
  MONTHLY_INTEREST_RATE: "comparableMonthlyInterestRate",
  YEARLY_INTEREST_RATE: "comparableYearlyInterestRate",
};

const HINT_CODE_STATES = {
  ERROR: "error",
  USER_CANCEL: "userCancel",
  OUTSTANDING_TRANSACTION: "outstandingTransaction",
  USER_SIGN: "userSign",
  START_FAILED: "startFailed",
};

const IMPORTANT_INFO_TYPE = {
  NO_ADDITIONS_REQUIRED: "NO_ADDITIONS_REQUIRED",
  REDUCED_GUARANTOR_COMMITMENT: "REDUCED_GUARANTOR_COMMITMENT",
  NO_GUARANTOR_REQUIRED: "NO_GUARANTOR_REQUIRED",
  FREE_EARLY_PAYMENT: "FREE_EARLY_PAYMENT",
  NO_LEASING_DOWN_PAYMENT: "NO_LEASING_DOWN_PAYMENT",
  NO_LIMIT_FEE: "NO_LIMIT_FEE",
  NO_SETUP_FEE: "NO_SETUP_FEE",
  EU_LOAN: "EU_LOAN",
  NO_WITHDRAWAL_FEE: "NO_WITHDRAWAL_FEE",
  NO_RECOURSE: "NO_RECOURSE",
  NO_AMORTIZATION: "NO_AMORTIZATION",
};

const isUrlRegex =
  /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;

const RESOURCE_TYPE = {
  GUARANTOR: "GUARANTOR",
  APPLYING_COMPANY: "APPLYING_COMPANY",
};

const FormatNumber = (value: number | bigint | null | undefined) => {
  if (value)
    return new Intl.NumberFormat("sv", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

  return "";
};

const numberFromFormat = (
  value: string | number | null | undefined,
): number | null => {
  // For input fields that are dealing with numbers,
  // the value from the onChange can be a string, formatted string, number, null, or undefined.
  // this converts the value to a number, or null if it's not a valid number

  // Handle null or undefined by returning null
  if (value === null || value === undefined) {
    return null;
  }

  // If the original value is a string, attempt to clean and convert it
  if (typeof value === "string") {
    // Remove all non-digit characters
    const cleanedValue = value.replace(/\D/g, "");

    // If the cleaned value is empty, return null
    if (cleanedValue === "") {
      return null;
    }

    // Convert the cleaned string to a number
    const numericValue = Number(cleanedValue);

    // If conversion results in NaN, return null
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  // If the value is already a number, return it as is
  if (typeof value === "number") {
    return value;
  }

  // Default case: return null if none of the above conditions are met
  return null;
};

const getBenefits = (
  benefits?: ILenderResponseBenefit[],
): ILenderResponseBenefit[] => {
  if (!benefits) return [];

  return benefits.map((benefit) => {
    const benefitToMap = {
      ...benefit,
    };

    switch (benefit.type) {
      case LenderResponseBenefitTypeEnum.NO_ADDITIONS_REQUIRED:
        benefitToMap.icon = FileCheck;
        break;

      case LenderResponseBenefitTypeEnum.REDUCED_GUARANTOR_COMMITMENT:
        benefitToMap.icon = ChartPie;
        break;

      case LenderResponseBenefitTypeEnum.NO_GUARANTOR_REQUIRED:
        benefitToMap.icon = UserX;
        break;

      case LenderResponseBenefitTypeEnum.FREE_EARLY_PAYMENT:
        benefitToMap.icon = HandCoins;
        break;

      case LenderResponseBenefitTypeEnum.NO_LEASING_DOWN_PAYMENT:
        benefitToMap.icon = HandCoins;
        break;

      case LenderResponseBenefitTypeEnum.NO_WITHDRAWAL_FEE:
        benefitToMap.icon = HandCoins;
        break;

      case LenderResponseBenefitTypeEnum.EU_LOAN:
        benefitToMap.icon = ImportantEuLoanIcon;
        break;

      case LenderResponseBenefitTypeEnum.NO_RECOURSE:
        benefitToMap.icon = FileLock;
        break;

      case LenderResponseBenefitTypeEnum.NO_AMORTIZATION:
        benefitToMap.icon = HandCoins;
        break;

      default:
        break;
    }

    return benefitToMap;
  });
};

const isIOS = () => !!window.navigator.userAgent.match(/iPhone|iPod|iPad/i);

const isMobile = () =>
  window.navigator.userAgent.match(
    /Android|webOS|iPhone|iPod|iPad|Blackberry/i,
  );

const isValidEmail = (email: string) => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  return re.test(email);
};

interface IValidatorReturnType {
  formatted: string;
  compact: string;
  isValid: boolean;
}

const convertTo12DigitSSN = (ssn: string) => {
  // Extract the year, month, and day from the SSN
  const parts = ssn.split("-");
  const datePart = parts[0];
  const serialPart = parts[1];

  if (!datePart || !serialPart) {
    throw new Error("Invalid SSN format");
  }

  const year = Number.parseInt(datePart.slice(0, 2), 10); // Extract the year (YY)
  const currentYear = new Date().getFullYear(); // Get current year, e.g., 2024
  const currentCentury = Math.floor(currentYear / 100); // E.g., 20

  // Deduce the full year (YYYY)
  let fullYear;

  // Current two-digit year to compare with
  const currentTwoDigitYear = currentYear % 100;

  if (year > currentTwoDigitYear) {
    // If year is greater than current two-digit year, assume it's from the previous century
    fullYear = (currentCentury - 1) * 100 + year; // 1900s
  } else {
    // Otherwise, assume it's from the current century
    fullYear = currentCentury * 100 + year; // 2000s
  }

  // Return the 12-digit SSN
  return `${fullYear}${datePart.slice(2)}-${serialPart}`;
};

const SESsnValidator = (ssn: string): IValidatorReturnType => {
  const stdNumSESsn = stdnum[CountryCode.SE]?.personnummer;
  if (!stdNumSESsn) throw new Error("Swedish SSN validator not available");

  // always needs a formated SSN to validate
  const isValid =
    ssn.length > 0
      ? stdNumSESsn.validate(stdNumSESsn.format(ssn)).isValid
      : false;
  let formatted = ssn;

  if (isValid) {
    formatted = stdNumSESsn.format(ssn);

    // Currently the valid formatted always returns the 10 digit format (plus the dash)
    // But we always want to work with the 12 digit format
    if (formatted.length === 11) {
      formatted = convertTo12DigitSSN(formatted);
    }
  }
  const compact = formatted.replace(/-/g, "");

  return {
    formatted,
    compact,
    isValid,
  };
};

const ssnValidator = (
  value: string,
  countryCode: CountryCode,
): IValidatorReturnType => {
  switch (countryCode) {
    case CountryCode.SE:
      return SESsnValidator(value);
    case CountryCode.FI: {
      const fiHetu = stdnum[countryCode]?.hetu;
      if (!fiHetu) throw new Error("Finnish SSN validator not available");
      return {
        formatted: fiHetu.format(value),
        compact: fiHetu.compact(value),
        isValid: fiHetu.validate(value).isValid,
      };
    }
    default:
      throw new Error(`Country code [${countryCode}] not supported`);
  }
};

const isValidUrl = (urlString: string) => {
  return !!urlString && urlString.match(isUrlRegex);
};

const NormalizeRequestedPaymentTimeMonths = (months: number) => {
  const { t } = getI18n();

  const numberOfYears = Math.floor(months / 12);
  const numberOfMonths = months % 12;

  const monthPostfix = `${numberOfMonths} ${t("commons.month", { count: numberOfMonths })}`;

  const yearPrefix = `${numberOfYears} ${t("commons.year", { count: numberOfYears })}`;

  if (numberOfYears && !numberOfMonths) {
    return yearPrefix;
  }

  if (numberOfYears && numberOfMonths) {
    return `${yearPrefix} ${t("commons.and")} ${monthPostfix}`;
  }

  return monthPostfix;
};

const ScrollToElement = (
  element: string,
  offset: null | undefined,
  callback: (() => void) | undefined,
) => {
  let offsetValue;

  if (offset != null) {
    offsetValue = offset;
  } else {
    offsetValue = -140;
  }

  import("react-scroll")
    .then((module) => {
      const scroller = module.scroller;
      const Events = module.Events;

      scroller.scrollTo(element, {
        duration: 1000,
        delay: 100,
        smooth: true,
        offset: offsetValue,
      });

      if (callback) {
        Events.scrollEvent.register("end", () => {
          callback();
          Events.scrollEvent.remove("end");
        });
      }
    })
    .catch((error: unknown) => {
      console.error("Unable to load react-scroll module", error);
    });
};

const ScrollToTop = () => {
  import("react-scroll")
    .then((module) => {
      const Scroll = module.default;
      const scroll = Scroll.animateScroll;

      scroll.scrollToTop({
        duration: 1000,
        delay: 0,
        smooth: true,
      });
    })
    .catch((error: unknown) => {
      console.error("Unable to load react-scroll module", error);
    });
};

const termsOfServiceLink = (
  country: string,
  websiteUrl: string,
  lang: SupportedLanguage,
) => {
  const linksSE = (lang: SupportedLanguage) => {
    switch (lang) {
      case "sv":
      case "en":
      case "fi":
        return "terms-of-service";
      default: {
        const exhaustiveCheck: never = lang;
        console.error(exhaustiveCheck);
        return null;
      }
    }
  };

  const linksFI = (lang: SupportedLanguage) => {
    switch (lang) {
      case "fi":
      case "sv":
      case "en":
        return "kayttoehdot";
      default: {
        const exhaustiveCheck: never = lang;
        console.error(exhaustiveCheck);
        return null;
      }
    }
  };

  switch (country) {
    case "SE":
      return `${websiteUrl}/${linksSE(lang)}`;
    case "FI":
      return `${websiteUrl}/${linksFI(lang)}`;
    default:
      return `${websiteUrl}/terms-of-service`;
  }
};

export * from "./bids";
export * from "./country";
export * from "./fieldRequests";
export * from "./loanApplications";
export * from "./testConstants";

export {
  BANKID_FLOW_STATES,
  COMPANY_TYPE,
  CUSTOMER_RATES,
  FormatNumber,
  getBenefits,
  HINT_CODE_STATES,
  IMPORTANT_INFO_TYPE,
  isIOS,
  isMobile,
  isUrlRegex,
  isValidEmail,
  isValidUrl,
  NormalizeRequestedPaymentTimeMonths,
  NOTIFICATION_RECEIVER,
  numberFromFormat,
  RESOURCE_TYPE,
  ScrollToElement,
  ScrollToTop,
  ssnValidator,
  termsOfServiceLink,
};
